import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Existing design tokens (see index.css :root) — reused, not reinvented.
const LIGHT_FOREGROUND_TOKEN = '210 40% 98%';
const DARK_FOREGROUND_TOKEN = '222 47% 11%';

// A brand colour this light would wash out to white in dark mode once the
// +5% lift is applied, so cap it.
const DARK_MODE_MAX_LIGHTNESS = 85;
const DARK_MODE_MIN_LIGHTNESS = 20;

export function isValidHexColor(value) {
  return typeof value === 'string' && HEX_COLOR_PATTERN.test(value.trim());
}

// #abc -> aabbcc. Without this the 3-digit form parses to NaN below.
function normalizeHex(hex) {
  if (!isValidHexColor(hex)) return null;
  const raw = hex.trim().replace(/^#/, '');
  return raw.length === 3
    ? raw.split('').map(c => c + c).join('')
    : raw;
}

function luminance(channels) {
  return channels.map(value => value / 255)
    .map(value => value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4)
    .reduce((total, value, index) => total + value * [.2126, .7152, .0722][index], 0);
}

function foregroundForChannels(channels) {
  const background = luminance(channels);
  const ratio = foreground => (Math.max(background, foreground) + .05) / (Math.min(background, foreground) + .05);
  const light = ratio(luminance([248, 250, 252]));
  const dark = ratio(luminance([15, 23, 42]));
  if (Math.max(light, dark) >= 4.5) return light >= dark ? LIGHT_FOREGROUND_TOKEN : DARK_FOREGROUND_TOKEN;
  // Mid-tone brands can fail with both near-white and slate; black/white supplies
  // the remaining contrast without changing the tenant's background color.
  return ratio(0) >= ratio(1) ? '0 0% 0%' : '0 0% 100%';
}

export function foregroundTokenFor(hex) {
  const raw = normalizeHex(hex);
  if (!raw) return null;
  return foregroundForChannels([0, 2, 4].map(offset => parseInt(raw.slice(offset, offset + 2), 16)));
}

export function foregroundTokenForHSL({ h, s, l }) {
  const saturation = s / 100;
  const lightness = l / 100;
  const amplitude = saturation * Math.min(lightness, 1 - lightness);
  const channel = n => {
    const k = (n + h / 30) % 12;
    return 255 * (lightness - amplitude * Math.max(-1, Math.min(k - 3, 9 - k, 1)));
  };
  return foregroundForChannels([channel(0), channel(8), channel(4)]);
}

// Convert hex to HSL values for CSS custom properties
export function hexToHSL(hex) {
  const raw = normalizeHex(hex);
  if (!raw) return null;

  // Parse hex
  const r = parseInt(raw.substring(0, 2), 16) / 255;
  const g = parseInt(raw.substring(2, 4), 16) / 255;
  const b = parseInt(raw.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        h = 0;
    }
  }
  
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('eva_theme') || 'light';
    }
    return 'light';
  });
  
  const [brandColors, setBrandColors] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('eva_brand_colors');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {
          return null;
        }
      }
    }
    return null;
  });

  // Apply theme class to root element
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('eva_theme', theme);
  }, [theme]);
  
  // Apply brand colors as CSS custom properties
  useEffect(() => {
    if (!brandColors) return;
    
    const root = window.document.documentElement;
    
    // Apply primary color
    if (brandColors.primary) {
      const primaryHSL = hexToHSL(brandColors.primary);
      if (primaryHSL) {
        root.style.setProperty('--brand-primary', brandColors.primary);
        root.style.setProperty('--brand-primary-h', primaryHSL.h);
        root.style.setProperty('--brand-primary-s', `${primaryHSL.s}%`);
        // NOTE: the % unit is part of the value — index.css does
        // calc(var(--brand-primary-l) + 5%), which is invalid if this is unitless.
        root.style.setProperty('--brand-primary-l', `${primaryHSL.l}%`);
        // Clamped dark-mode lightness. The raw calc(l + 5%) clips toward white
        // for already-light brand colours.
        const darkL = Math.min(
          Math.max(primaryHSL.l + 5, DARK_MODE_MIN_LIGHTNESS),
          DARK_MODE_MAX_LIGHTNESS
        );
        root.style.setProperty('--brand-primary-l-dark', `${darkL}%`);
        // Keeps button text readable when a tenant picks a light brand colour.
        const foreground = foregroundTokenForHSL(primaryHSL);
        if (foreground) {
          root.style.setProperty('--brand-primary-fg', foreground);
          root.style.setProperty('--brand-primary-fg-dark', foregroundTokenForHSL({ ...primaryHSL, l: darkL }));
        }
      }
    }
    
    // Apply secondary color
    if (brandColors.secondary) {
      const secondaryHSL = hexToHSL(brandColors.secondary);
      if (secondaryHSL) {
        root.style.setProperty('--brand-secondary', brandColors.secondary);
        root.style.setProperty('--brand-secondary-h', secondaryHSL.h);
        root.style.setProperty('--brand-secondary-s', `${secondaryHSL.s}%`);
        root.style.setProperty('--brand-secondary-l', `${secondaryHSL.l}%`);
      }
    }
    
    // Save to localStorage for persistence
    localStorage.setItem('eva_brand_colors', JSON.stringify(brandColors));
  }, [brandColors]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };
  
  // Set brand colors from tenant data
  const applyBrandColors = useCallback((primaryColor, secondaryColor) => {
    if (primaryColor || secondaryColor) {
      setBrandColors({
        primary: primaryColor || '#6366f1',
        secondary: secondaryColor || '#8b5cf6',
      });
    }
  }, []);
  
  // Clear brand colors (reset to defaults)
  const clearBrandColors = useCallback(() => {
    setBrandColors(null);
    localStorage.removeItem('eva_brand_colors');
    const root = window.document.documentElement;
    root.style.removeProperty('--brand-primary');
    root.style.removeProperty('--brand-primary-h');
    root.style.removeProperty('--brand-primary-s');
    root.style.removeProperty('--brand-primary-l');
    root.style.removeProperty('--brand-primary-l-dark');
    root.style.removeProperty('--brand-primary-fg');
    root.style.removeProperty('--brand-primary-fg-dark');
    root.style.removeProperty('--brand-secondary');
    root.style.removeProperty('--brand-secondary-h');
    root.style.removeProperty('--brand-secondary-s');
    root.style.removeProperty('--brand-secondary-l');
  }, []);

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      toggleTheme,
      brandColors,
      applyBrandColors,
      clearBrandColors,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
